<template>
  <q-card bordered flat>
    <template v-if="advert">

      <q-card-section>
        <template v-if="!loading">
          <fade-out-text :max-height="200" :trashhold="100">
            <table class="c-spec-table">
              <tbody>
              <template v-for="(field, index) in cardFields" :key="index">
                <tr v-if="field.value">
                  <td>{{ field.label }}</td>
                  <td class="q-pl-sm">{{ field.value }}</td>
                </tr>
              </template>
              </tbody>
            </table>

            <div
              v-if="advert.vehicle?.equipment?.color_display || badgesFields.filter(obj => obj.value).length > 0"
              class="q-mt-lg flex q-gutter-xs"
            >
              <div v-if="advert.vehicle?.equipment?.color_display">
                <q-badge
                  class="q-pa-sm"
                  color="black" outline rounded>Farbe: {{
                    advert.vehicle?.equipment?.color_display
                  }}
                </q-badge>
              </div>

              <template v-for="(item, index) in badgesFields" :key="index">
                <div v-if="item.value">
                  <q-badge
                    class="q-pa-sm"
                    color="black"
                    outline rounded
                  >
                    {{ item.label }}
                  </q-badge>
                </div>
              </template>
            </div>
          </fade-out-text>
        </template>
        <template v-else>
          <div class="flex column">
            <div v-for="i in 12" :key="i" class="flex q-gutter-md">
              <div style="flex: 1">
                <q-skeleton :width="`${Math.floor(Math.random() * (100 - 60 + 1)) + 60}%`" type="text"/>
              </div>
              <div style="flex: 1">
                <q-skeleton :width="`${Math.floor(Math.random() * (80 - 40 + 1)) + 40}%`" type="text"/>
              </div>
            </div>
          </div>

          <div class="q-mt-md">
            <div class="flex q-gutter-sm">
              <q-skeleton v-for="i in 5" :key="i" rounded type="QChip"/>
            </div>
          </div>
        </template>
      </q-card-section>
    </template>

    <template v-else>
      <q-card-section>
        <div class="flex column">
          <div v-for="i in 12" :key="i" class="flex q-gutter-md">
            <div style="flex: 1">
              <q-skeleton :width="`${Math.floor(Math.random() * (100 - 60 + 1)) + 60}%`" type="text"/>
            </div>
            <div style="flex: 1">
              <q-skeleton :width="`${Math.floor(Math.random() * (80 - 40 + 1)) + 40}%`" type="text"/>
            </div>
          </div>

          <div class="q-mt-md">
            <div class="flex q-gutter-sm">
              <q-skeleton v-for="i in 5" :key="i" rounded type="QChip"/>
            </div>
          </div>
        </div>
      </q-card-section>
    </template>

  </q-card>
</template>

<script>
import {ref} from 'vue';
import OptionFieldsParser from 'src/components/utils/OptionFieldsParser';
import {api} from 'boot/axios';
import FadeOutText from 'components/qutils/FadeOutText.vue';

const badgesFieldNames = [
  'vehicle__equipment__metallic',
  'vehicle__equipment__abs',
  'vehicle__equipment__esp',
  'vehicle__equipment__cruise_control',
  'vehicle__equipment__trailer_hitch',
  'vehicle__equipment__ac',
  'vehicle__equipment__stationary_heating',
  'vehicle__equipment__seat_heating',
  'vehicle__equipment__parking_assistance',
  'vehicle__equipment__rear_view_camera',
  'vehicle__all_wheel_drive',
  'vehicle__particle_filter',
]

const cardFieldNames = [
  'vehicle__mileage',
  'vehicle__car_type_display',
  'vehicle__build_type_display',
  'vehicle__admission_display',
  'vehicle__has_damage',
  'vehicle__engine_power_kw',
  'vehicle__engine_ccm',
  'vehicle__registration',
  'vehicle__year',
  'vehicle__number_of_owners',
  'vehicle__beds',
  'vehicle__seats',
  'vehicle__length',
  'vehicle__maximum_weight_kg',
  'vehicle__tare_weight_kg',
  'vehicle__is_oldtimer',
  'vehicle__general_inspection_until',
  'vehicle__general_inspection_renew',
  'vehicle__fuel_type_display',
  'vehicle__transmission_display',
  'vehicle__environmental_badge_display',
  'vehicle__emission_class_display',
  'vehicle__drive_shaft_position_display',
  'vehicle__roof_system_display',
  'vehicle__is_serviced',
  'vehicle__axles',
]


export default {
  name: 'AdvertEquipmentCard',
  components: {FadeOutText},
  props: {
    advert: {required: true},
  },
  setup(props) {
    const badgesFields = ref([])
    const cardFields = ref([])
    const loading = ref(true)

    api.options('adverts/').then(resp => {
      const optionFields = new OptionFieldsParser(resp.data.actions.POST, props.advert)
      badgesFields.value = optionFields.getFields(badgesFieldNames)

      optionFields.getFields(cardFieldNames).forEach(field => {

        if (field.fieldNotation === 'vehicle__mileage' && field.value) {
          field.value = `${Intl.NumberFormat().format(field.value)} km`
        }

        if (field.fieldNotation === 'vehicle__maximum_weight_kg' && field.value) {
          field.value = `${Intl.NumberFormat().format(field.value)} kg`
        }

        if (field.fieldNotation === 'vehicle__engine_power_kw' && field.value) {
          const ps = optionFields.getFieldValue('vehicle__engine_power_ps')
          const _vals = []
          if (field.value) {
            _vals.push(`${field.value} kW`)
          }
          if (ps) {
            _vals.push(`${ps} PS`)
          }
          field.value = _vals.join(' / ')
        }

        // change boolean to "Ja/Nein/undefined"
        if (field.type === 'boolean') {
          field.value = field.value ? 'Ja' : field.value === false ? 'Nein' : undefined
        }

        cardFields.value.push({
          label: field.label,
          value: field.value
        })
      })

      const l = optionFields.getFieldValue('vehicle__length')
      const h = optionFields.getFieldValue('vehicle__height')
      const w = optionFields.getFieldValue('vehicle__width')
      const _l = optionFields.getFieldValue('vehicle__size_length')
      const _h = optionFields.getFieldValue('vehicle__size_height')
      const sizingSymbol = {
        label: 'Größenangabe',
        value: `${_l || ''}${_h || ''}`
      }
      cardFields.value.push(sizingSymbol)
      const sizing = {
        label: 'Maße',
        value: `${l || '-'} l x ${w || '-'} b x ${h || '-'} h`
      }
      cardFields.value.push(sizing)
    }).finally(() => {
      loading.value = false
    })

    return {
      badgesFields,
      cardFields,
      loading,
    }
  }
}
</script>

<style lang="scss" scoped>
.c-spec-table {
  width: 100%;
  border-spacing: 0;

  tr:nth-child(even) {
    background-color: $grey-2;
  }
}
</style>
